import { IS_BROWSER } from 'core/constants';
import { loadSessionStorage, saveSessionStorage } from 'lib/storage';

const UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
] as const;

const UTM_TIMESTAMP_KEY = 'utm_set_at';

type UtmKey = (typeof UTM_KEYS)[number];

export type UtmParams = Partial<Record<UtmKey, string>> & {
    utm_set_at?: string;
};

/**
 * Reads UTM parameters from the current URL and persists them to sessionStorage
 * so they can be sent with later events in the same session (e.g. booking steps).
 * Call whenever the user lands on a page with UTMs so we retain attribution even
 * if GA4 session source was already set (e.g. returning tab).
 *
 * We also record the first time in this session that UTMs were seen in
 * `utm_set_at` (ISO string) for debugging/secondary attribution checks.
 */
export function captureUtmParamsFromUrl(): void {
    if (!IS_BROWSER || typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    let hasAny = false;
    const stored: Partial<Record<UtmKey, string>> = {};

    for (const key of UTM_KEYS) {
        const value = params.get(key);
        if (value) {
            stored[key] = value;
            hasAny = true;
        }
    }

    if (!hasAny) return;

    for (const [key, value] of Object.entries(stored)) {
        if (value) saveSessionStorage(key, value);
    }

    const existingTimestamp = loadSessionStorage<string>(UTM_TIMESTAMP_KEY);
    if (!existingTimestamp) {
        const nowIso = new Date().toISOString();
        saveSessionStorage(UTM_TIMESTAMP_KEY, nowIso);
    }
}

/**
 * Returns UTM parameters stored in sessionStorage (from a previous captureUtmParamsFromUrl
 * or from another source). Used to attach to events so GA4 has a reliable
 * event-level dimension for attribution regardless of session source.
 */
export function getStoredUtmParams(): UtmParams {
    if (!IS_BROWSER || typeof sessionStorage === 'undefined') return {};

    const result: UtmParams = {};
    for (const key of UTM_KEYS) {
        const value = loadSessionStorage<string>(key);
        if (value) result[key] = value;
    }

    const timestamp = loadSessionStorage<string>(UTM_TIMESTAMP_KEY);
    if (timestamp) {
        result.utm_set_at = timestamp;
    }

    return result;
}
